import { useEffect, useMemo, useState } from "react";
import { safeHtml } from "@/lib/sanitize";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Mail, Copy, Trash2, Save, RotateCcw, Send, Eye, Pencil } from "lucide-react";
import {
  deleteEmailTemplate,
  EMAIL_TEMPLATE_KEYS,
  listEmailTemplates,
  upsertEmailTemplate,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { authHeaders } from "@/lib/api/_shared";
import { useViewAsClient } from "@/lib/viewAsClient";
import { useFormVisibility, hiddenPlaceholders } from "@/lib/formVisibility";

/**
 * Per-tenant email template overrides.
 *
 * The default templates live in lbs_email_templates with client_id=NULL.
 * A tenant can copy a default to their own row (client_id=<theirs>) and
 * edit it. When the SMTP cron renders an email, the per-tenant row wins
 * over the global default for that template_key.
 *
 * Super-admins use the top-nav "View as client" picker to choose which
 * tenant's overrides they want to manage.
 */
export default function EmailTemplates() {
  const { assignments } = useAuth();
  const { viewAsClientId, isSuperAdmin } = useViewAsClient();
  const myClientId = assignments?.find((a) => a.client_id)?.client_id || "";
  const targetClientId = isSuperAdmin ? viewAsClientId : myClientId;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(EMAIL_TEMPLATE_KEYS[0]);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const r = await listEmailTemplates({ clientId: targetClientId || null });
      if (cancelled) return;
      if (r.error) toast.error(r.error.message);
      setTemplates(r.data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [targetClientId, tick]);

  // Resolve which row is currently active for the selected key: tenant override beats default.
  const { defaultRow, overrideRow, activeRow } = useMemo(() => {
    const def = templates.find((t) => t.template_key === selectedKey && t.client_id === null);
    const ovr = templates.find((t) => t.template_key === selectedKey && t.client_id === targetClientId);
    return { defaultRow: def, overrideRow: ovr, activeRow: ovr || def };
  }, [templates, selectedKey, targetClientId]);

  // Editor remounts when key/tenant changes (via parent key) — no useEffect-to-setState needed.
  return (
    <div data-testid="email-templates-page" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Mail className="h-6 w-6" /> Email templates
          </h1>
          <p className="text-sm text-slate-500">
            {targetClientId ? (
              <>Per-tenant overrides on top of the system defaults. <code className="text-xs">{`{{token}}`}</code> placeholders are substituted at send-time.</>
            ) : (
              <>System defaults (read-only here). {isSuperAdmin && <em>Pick a tenant in the top nav to author overrides.</em>}</>
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-4">
        {/* Template list */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Templates</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-3 text-xs text-slate-400">Loading…</div>
            ) : (
              <ul className="divide-y text-sm">
                {EMAIL_TEMPLATE_KEYS.map((k) => {
                  const ovr = templates.find((t) => t.template_key === k && t.client_id === targetClientId);
                  const def = templates.find((t) => t.template_key === k && t.client_id === null);
                  const exists = !!(ovr || def);
                  return (
                    <li
                      key={k}
                      data-testid={`tmpl-row-${k}`}
                      onClick={() => setSelectedKey(k)}
                      className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-2 ${
                        selectedKey === k ? "bg-slate-900 text-white" : "hover:bg-slate-50"
                      }`}
                    >
                      <span className="font-mono text-xs truncate">{k}</span>
                      {ovr && (
                        <Badge className={`text-[9px] ${selectedKey === k ? "bg-amber-200 text-amber-900" : "bg-amber-100 text-amber-700"}`}>
                          override
                        </Badge>
                      )}
                      {!exists && (
                        <Badge variant="outline" className="text-[9px] text-slate-400">missing</Badge>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Editor — remount on key change to reset state from props */}
        <TemplateEditor
          key={`${targetClientId || "global"}::${selectedKey}::${activeRow?.id || "none"}`}
          targetClientId={targetClientId}
          selectedKey={selectedKey}
          defaultRow={defaultRow}
          overrideRow={overrideRow}
          activeRow={activeRow}
          isSuperAdmin={isSuperAdmin}
          onSaved={refresh}
        />
      </div>
    </div>
  );
}

function TemplateEditor({ targetClientId, selectedKey, defaultRow, overrideRow, activeRow, isSuperAdmin, onSaved }) {
  const { session } = useAuth();
  const myEmail = session?.user?.email || "";
  const [draft, setDraft] = useState({
    subject:   activeRow?.subject   || "",
    body_html: activeRow?.body_html || "",
    body_text: activeRow?.body_text || "",
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [mode, setMode] = useState("edit"); // "edit" | "preview"

  // Preview state — populated via /api/email/preview (backend uses the
  // real _render() from drainers/outbox_drainer, so what admins see
  // matches what customers will receive, incl. the P1 SEC escape rules).
  const [previewCtxType, setPreviewCtxType] = useState("synthetic"); // "synthetic" | "real"
  const [previewBookingId, setPreviewBookingId] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const isOverride = !!overrideRow;
  const canEdit = !!targetClientId;
  const vis = useFormVisibility(targetClientId);
  const orphanTokens = hiddenPlaceholders(`${draft.subject} ${draft.body_html} ${draft.body_text}`, vis);
  const setField = (k, v) => { setDraft((d) => ({ ...d, [k]: v })); setDirty(true); };

  const refreshPreview = async () => {
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/email/preview`;
      const r = await fetch(url, {
        method: "POST",
        headers: await authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          subject:      draft.subject,
          body_html:    draft.body_html,
          body_text:    draft.body_text,
          context_type: previewCtxType,
          booking_id:   previewCtxType === "real" ? previewBookingId : null,
          client_id:    targetClientId,
        }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.detail || `Preview failed (${r.status})`);
      setPreviewData(body);
    } catch (e) {
      setPreviewError(String(e.message || e));
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Auto-fetch synthetic preview when entering Preview mode or when
  // switching between context types with a valid booking id.
  useEffect(() => {
    if (mode !== "preview") return;
    if (previewCtxType === "real" && !previewBookingId) return;
    refreshPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, previewCtxType, selectedKey, isOverride]);


  const onSave = async () => {
    if (!canEdit) {
      toast.error(isSuperAdmin
        ? "Pick a tenant via the top-nav 'View as client' picker first."
        : "No tenant scope.");
      return;
    }
    setSaving(true);
    const payload = isOverride
      ? { id: overrideRow.id, ...draft }
      : { client_id: targetClientId, template_key: selectedKey, ...draft };
    const { error } = await upsertEmailTemplate(payload);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(isOverride ? "Template updated." : "Tenant override created.");
    setDirty(false);
    onSaved();
  };

  const onCopyFromDefault = () => {
    if (!defaultRow) { toast.error("No default to copy."); return; }
    setDraft({
      subject:   defaultRow.subject   || "",
      body_html: defaultRow.body_html || "",
      body_text: defaultRow.body_text || "",
    });
    setDirty(true);
    toast.message("Default copied into editor. Click Save to persist.");
  };

  const onRevertToDefault = async () => {
    if (!overrideRow) return;
    if (!window.confirm("Delete your tenant's override and fall back to the global default?")) return;
    const { error } = await deleteEmailTemplate(overrideRow.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Reverted to default.");
    onSaved();
  };

  const onSendTest = async () => {
    if (!canEdit) { toast.error("Pick a tenant first."); return; }
    if (!myEmail) { toast.error("Your account has no email address."); return; }
    if (dirty && !window.confirm("You have unsaved changes — send test using the LAST SAVED version anyway?")) return;
    try {
      setTesting(true);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/send-test-email`;
      const { supabase } = await import("@/lib/supabaseClient");
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          client_id:    targetClientId,
          template_key: selectedKey,
          to_email:     myEmail,
        }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.error || "Test send failed");
      if (j.delivered) toast.success(`Test email sent to ${myEmail}.`);
      else             toast.error(`Test queued but not delivered — check SMTP settings (failed=${j.failed}).`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm flex flex-wrap items-center gap-2 justify-between">
          <span><code className="font-mono">{selectedKey}</code></span>
          <div className="flex items-center gap-2">
            {/* Edit/Preview toggle */}
            <div className="inline-flex rounded-md border bg-slate-50 p-0.5" data-testid="tmpl-mode-toggle">
              <button
                type="button"
                onClick={() => setMode("edit")}
                className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs ${
                  mode === "edit" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"
                }`}
                data-testid="tmpl-mode-edit"
              >
                <Pencil className="h-3 w-3" /> Edit
              </button>
              <button
                type="button"
                onClick={() => setMode("preview")}
                className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs ${
                  mode === "preview" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"
                }`}
                data-testid="tmpl-mode-preview"
              >
                <Eye className="h-3 w-3" /> Preview
              </button>
            </div>
            {isOverride ? (
              <Badge className="bg-amber-100 text-amber-700 text-[10px]" data-testid="tmpl-status-override">tenant override</Badge>
            ) : defaultRow ? (
              <Badge variant="secondary" className="text-[10px]" data-testid="tmpl-status-default">default</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] text-rose-500">missing</Badge>
            )}
            {canEdit && !isOverride && defaultRow && (
              <Button size="sm" variant="outline" onClick={onCopyFromDefault} data-testid="tmpl-copy-default">
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy default to edit
              </Button>
            )}
            {canEdit && isOverride && (
              <Button size="sm" variant="ghost" onClick={onRevertToDefault} data-testid="tmpl-revert">
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Revert
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {mode === "edit" ? (
          <>
            <Field label="Subject">
              <Input
                value={draft.subject}
                onChange={(e) => setField("subject", e.target.value)}
                disabled={!canEdit}
                data-testid="tmpl-subject"
              />
            </Field>
            <Field label="Body (HTML)">
              <Textarea
                rows={14}
                value={draft.body_html}
                onChange={(e) => setField("body_html", e.target.value)}
                disabled={!canEdit}
                className="font-mono text-xs"
                placeholder="<html>...</html>"
                data-testid="tmpl-html"
              />
            </Field>
            <Field label="Body (plain text fallback)">
              <Textarea
                rows={6}
                value={draft.body_text}
                onChange={(e) => setField("body_text", e.target.value)}
                disabled={!canEdit}
                className="font-mono text-xs"
                data-testid="tmpl-text"
              />
            </Field>
            {orphanTokens.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="tmpl-hidden-field-warning">
                This template uses {orphanTokens.map((t) => <code key={t} className="mx-0.5">{`{{${t}}}`}</code>)} but
                {orphanTokens.length === 1 ? " that field is" : " those fields are"} switched off in this tenant&apos;s booking form,
                so {orphanTokens.length === 1 ? "it" : "they"} will usually be blank in sent emails.
              </div>
            )}
            <div className="text-[11px] text-slate-500">
              Available tokens: <code>{`{{reference_number}}`}</code>, <code>{`{{status}}`}</code>,{" "}
              <code>{`{{direction}}`}</code>, <code>{`{{scheduled_start}}`}</code>,{" "}
              <code>{`{{sender_name}}`}</code>, <code>{`{{sender_company}}`}</code>,{" "}
              <code>{`{{receiver_name}}`}</code>, <code>{`{{receiver_company}}`}</code>,{" "}
              <code>{`{{rejection_reason}}`}</code>, <code>{`{{site_rules_url}}`}</code>
              <div className="mt-1" data-testid="template-new-tokens">
                Load &amp; vehicle: {["title", "po_number", "haulier_company", "booked_by_broker", "rego_line",
                  "rego_prime_mover", "rego_trailer_1", "vehicle_type_label", "payload_line", "pallets_line",
                  "load_block_text", "load_block_html"].map((t) => <code key={t} className="mx-0.5">{`{{${t}}}`}</code>)}
                <br />Declarations &amp; status: {["declarations_text", "declarations_html", "declarations_list", "declarations_outstanding",
                  "approval_status", "site_time_label"].map((t) => <code key={t} className="mx-0.5">{`{{${t}}}`}</code>)}
                <br />When &amp; where: {["when_where_html", "when_where_text", "when_where_subject"].map((t) => <code key={t} className="mx-0.5">{`{{${t}}}`}</code>)}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 flex items-center justify-between gap-2">
              <div>
                Preview rendered by the real outbox pipeline
                (<code>drainers.outbox_drainer._render</code>). HTML body
                is escaped per the P1 SEC fix — any raw
                <code className="mx-1">&lt;script&gt;</code>
                in a context value renders as literal text.
              </div>
              <div className="inline-flex rounded-md border bg-white p-0.5 shrink-0" data-testid="preview-ctx-toggle">
                <button
                  type="button"
                  onClick={() => setPreviewCtxType("synthetic")}
                  className={`px-2 py-1 text-xs rounded ${
                    previewCtxType === "synthetic" ? "bg-slate-900 text-white" : "text-slate-600"
                  }`}
                  data-testid="preview-ctx-synthetic"
                >Synthetic</button>
                <button
                  type="button"
                  onClick={() => setPreviewCtxType("real")}
                  className={`px-2 py-1 text-xs rounded ${
                    previewCtxType === "real" ? "bg-slate-900 text-white" : "text-slate-600"
                  }`}
                  data-testid="preview-ctx-real"
                >Real booking</button>
              </div>
            </div>
            {previewCtxType === "real" && (
              <div className="flex items-end gap-2">
                <Field label="Booking id (UUID)">
                  <Input
                    value={previewBookingId}
                    onChange={(e) => setPreviewBookingId(e.target.value)}
                    placeholder="paste an lbs_bookings.id UUID…"
                    className="font-mono text-xs"
                    data-testid="preview-booking-id"
                  />
                </Field>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refreshPreview}
                  disabled={!previewBookingId || previewLoading}
                  data-testid="preview-load-real"
                >
                  {previewLoading ? "Loading…" : "Load"}
                </Button>
              </div>
            )}
            {previewError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="preview-error">
                {previewError}
              </div>
            )}
            <Field label="Subject (preview)">
              <div
                className="rounded-md border bg-white px-3 py-2 text-sm"
                data-testid="tmpl-preview-subject"
              >
                {previewData?.rendered_subject || <span className="text-slate-400">(empty)</span>}
              </div>
            </Field>
            <Field label="Body (preview)">
              <div
                className="rounded-md border bg-white px-4 py-3 text-sm prose prose-sm max-w-none min-h-[280px]"
                data-testid="tmpl-preview-html"
                dangerouslySetInnerHTML={safeHtml(previewData?.rendered_body_html || "<em class='text-slate-400'>(empty)</em>")}
              />
            </Field>
            {draft.body_text && (
              <Field label="Plain text (preview)">
                <pre
                  className="whitespace-pre-wrap rounded-md border bg-white px-3 py-2 text-xs text-slate-700"
                  data-testid="tmpl-preview-text"
                >{previewData?.rendered_body_text || ""}</pre>
              </Field>
            )}
            <div className="flex items-center justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={refreshPreview}
                disabled={previewLoading || (previewCtxType === "real" && !previewBookingId)}
                data-testid="preview-refresh"
              >
                {previewLoading ? "Rendering…" : "Refresh preview"}
              </Button>
            </div>
          </>
        )}
        <div className="flex items-center justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onSendTest} disabled={!canEdit || testing} data-testid="tmpl-send-test">
            <Send className="h-4 w-4 mr-1.5" /> {testing ? "Sending…" : `Send test → ${myEmail || "me"}`}
          </Button>
          <Button onClick={onSave} disabled={!canEdit || !dirty || saving} data-testid="tmpl-save">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
